import React, { useContext, useEffect, useState } from "react";
import { normalizeSearch, popFrontURL } from "../../utils";

import { ProductPrice, ProductStock } from "@sbi-international/boutik.gatsby.plugin.components";
import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { IProduct, ServicesContext } from "@sbi-international/boutik.gatsby.plugin.services";
import { AccessorySingle } from "./singles/AccessorySingle";
import { ProductSingle } from "./singles/ProductSingle";
import { VentingSingle } from "./singles/VentingSingle";
import { AllProductsNodeModel } from "../../models/allProductsNodeModel";
import Loading from "../primitives/LoadingFont";
import GatsbyImageWrapper from "../GatsbyImageWrapper";

export default function SearchList(props: {
    searchString: string;
    isSearchListOpen: boolean;
    allProducts: Queries.allProductsNodeFragmentFragment[];
    t: any;
    setSearchListOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const [products, setProducts] = useState<AllProductsNodeModel[]>([]);
    const [isLoading, setLoading] = useState<boolean>(true);
    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.magento.productService;
    const [skus, setSkus] = useState<string[]>([]);
    const [isAfterFetch, setAfterFetch] = useState(false);
    const { navigateTranslated, currentLocale } = usePageLocalization();
    const { allProducts, isSearchListOpen, searchString, setSearchListOpen, t } = props;
    useEffect(() => {
        if (searchString.length >= 2) {
            fetch(`/search?q=${normalizeSearch(searchString)}`)
                .then((response) => response.json())
                .then((data) => {
                    let searchResults = data?.body?.hits?.hits;
                    searchResults = searchResults.slice(0, Math.min(searchResults.length, 50));
                    searchResults = searchResults?.filter((e: any) => {
                        const category = e?._source?.category?.["en-CA"]?.toLowerCase();
                        return ["accessories", "heaters", "venting", "parts", "options"].some(
                            (str) => category.includes(str)
                        );
                    });
                    setSkus(searchResults?.map((result: any) => result?._source?.code));
                    setSearchListOpen(true);
                });
        } else {
            setSearchListOpen(false);
        }
    }, [searchString, setSearchListOpen, setSkus, setProducts]);

    useEffect(() => {
        const prodTmp: AllProductsNodeModel[] = [];
        if (skus && skus.length > 0 && productService) {
            setLoading(true);
            productService.operations
                .getProducts(skus)
                .then((productsData) => {
                    for (const sku of skus) {
                        const productFind = allProducts.find((product) => product.code === sku);

                        if (
                            productFind &&
                            ((currentLocale.includes("CA") &&
                                Array.isArray(productFind?.attributes?.available_country?.values) &&
                                productFind?.attributes?.available_country?.values?.some(
                                    (value: { [x: string]: string }) =>
                                        value[currentLocale] !== "United States"
                                )) ||
                                (currentLocale.includes("US") &&
                                    Array.isArray(
                                        productFind?.attributes?.available_country?.values
                                    ) &&
                                    productFind?.attributes?.available_country?.values?.some(
                                        (value: { [x: string]: string }) =>
                                            value[currentLocale] !== "Canada"
                                    )) ||
                                !productFind?.attributes?.available_country?.values)
                        ) {
                            let newProdTmp: AllProductsNodeModel = { static: productFind };
                            const prodLiveFind = productsData.find(
                                (productData) => productData.sku === sku
                            );
                            if (prodLiveFind) {
                                newProdTmp.live = prodLiveFind;
                            }
                            prodTmp.push(newProdTmp);
                        }
                    }

                    setAfterFetch(true);

                    setProducts(prodTmp);
                })
                .catch(() => {
                    setAfterFetch(true);
                    setProducts(prodTmp);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [allProducts, skus, currentLocale]);

    if (isSearchListOpen) {
        return (
            <div className="search-dropdown">
                {skus && skus.length > 0 ? (
                    <>
                        {isLoading ? (
                            <div className="search-loading">
                                <Loading />
                            </div>
                        ) : (
                            <>
                                {products.map((product: AllProductsNodeModel, index: number) => {
                                    const category = getItemKey(
                                        product?.static.slug?.en_CA ?? "",
                                        null
                                    )?.key;

                                    if (
                                        (category === "accessories" || category === "options") &&
                                        product?.static.code
                                    ) {
                                        if (product?.static.fields?.images) {
                                            return (
                                                <AccessorySingle key={index} product={product} />
                                            );
                                        }
                                        return undefined;
                                    } else if (category === "heaters" && product?.static.code) {
                                        return (
                                            <ProductSingle
                                                key={index}
                                                product={product}
                                                isDiscontinued={
                                                    !!product?.static.slug?.["en_CA"]?.includes(
                                                        "discontinued"
                                                    )
                                                }
                                            />
                                        );
                                    } else if (category === "venting" && product?.static.code) {
                                        return <VentingSingle key={index} product={product} />;
                                    } else if (category === "parts") {
                                        return (
                                            <PartSingle
                                                key={`search-list-partsingle-${index}`}
                                                product={product}
                                            />
                                        );
                                    } else return undefined;
                                })}
                                {products.length > 15 && (
                                    <div
                                        className="see-more"
                                        onClick={() =>
                                            navigateTranslated(`/search?q=${searchString}`)
                                        }
                                    >
                                        {t("search.more")}
                                    </div>
                                )}{" "}
                            </>
                        )}
                    </>
                ) : (
                    isAfterFetch && (
                        <p style={{ textAlign: "start", margin: "0.8rem" }}>
                            {t("search.no-results")}
                        </p>
                    )
                )}
            </div>
        );
    } else {
        return <div style={{ display: "none" }}></div>;
    }
}

function getItemKey(slugs: string | undefined, item: any): { key: string; item: any } {
    if (!slugs) {
        return { key: "NR", item };
    }
    if (slugs.includes("/cms/blog")) {
        return { key: "cms", item };
    } else if (slugs.includes("/accessories/")) {
        return { key: "accessories", item };
    } else if (slugs.includes("/archives/")) {
        return { key: "archives", item };
    } else if (slugs.includes("/heaters/") || slugs.includes("/discontinued/")) {
        return { key: "heaters", item };
    } else if (slugs.includes("/parts/")) {
        return { key: "parts", item };
    } else if (slugs.includes("/venting/")) {
        return { key: "venting", item };
    } else if (slugs.includes("/options/")) {
        return { key: "options", item };
    } else {
        return { key: "NR", item };
    }
}

function PartSingle(props: { product: AllProductsNodeModel }) {
    const { product } = props;
    const { navigateTranslated, currentLocale } = usePageLocalization();

    const navigateToPartSingle = (product: Queries.allProductsNodeFragmentFragment) => {
        if (product) {
            const link = popFrontURL(product?.slug?.[currentLocale], 1);
            if (link) {
                navigateTranslated(link);
            }
        }
    };

    return (
        <div
            className="product search-part"
            onClick={() => {
                navigateToPartSingle(product.static);
            }}
        >
            <div className="product-image">
                <GatsbyImageWrapper
                    className="product-img-wrapper"
                    imgStyle={{ objectFit: "contain" }}
                    fields={product?.static.fields}
                    imageIndex={1}
                />
            </div>
            <div className="product-info">
                <span className="code">{product.static.code}</span>
                <span className="title">{product?.static.title?.[currentLocale]}</span>
                {product?.live?.currentStock.status !== "discontinued" && (
                    <ProductPrice
                        currentPrice={product?.live?.currentPrice}
                        prefixClassName="listing-accessory-single"
                    />
                )}
                <ProductStock
                    currentStock={product?.live?.currentStock}
                    type="small"
                    isCustomMade={false}
                />
            </div>
        </div>
    );
}
